/**
 * VendorSourceFormModal - Create/Edit vendor sources
 * Part of the complete product lifecycle: Vendor → Vendor Item → Product
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Loader2, Truck, Save, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VENDOR_TYPES = [
  { value: 'drop_ship', label: 'Drop Ship', description: 'Ships directly to customer' },
  { value: 'wholesale', label: 'Wholesale', description: 'Bulk purchase for resale' },
  { value: 'white_label', label: 'White Label', description: 'Your own brand/products' }
];

const VendorSourceFormModal = ({ open, onOpenChange, vendorId = null, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    vendor_type: 'wholesale',
    website: '',
    default_markup_percent: 100,
    contact: {
      name: '',
      email: '',
      phone: ''
    },
    notes: ''
  });

  const isEditMode = !!vendorId;

  // Load vendor function
  const loadVendor = async (id) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/catalog/vendor-sources/${id}`);
      if (res.ok) {
        const data = await res.json();
        setFormData({
          name: data.name || '',
          code: data.code || '',
          vendor_type: data.vendor_type || 'wholesale',
          website: data.website || '',
          default_markup_percent: data.default_markup_percent || 100,
          contact: data.contact || { name: '', email: '', phone: '' },
          notes: data.notes || ''
        });
      }
    } catch (error) {
      console.error('Error loading vendor:', error);
      toast.error('Failed to load vendor');
    }
    setLoading(false);
  };

  // Load vendor data if editing
  useEffect(() => {
    if (open && vendorId) {
      loadVendor(vendorId);
    } else if (open && !vendorId) {
      // Reset form for new vendor
      setFormData({
        name: '',
        code: '',
        vendor_type: 'wholesale',
        website: '',
        default_markup_percent: 100,
        contact: {
          name: '',
          email: '',
          phone: ''
        },
        notes: ''
      });
    }
  }, [open, vendorId]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleContactChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      contact: { ...prev.contact, [field]: value }
    }));
  };

  // Auto-generate code from name
  const generateCode = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .substring(0, 4);
  };

  const handleNameChange = (name) => {
    handleChange('name', name);
    if (!isEditMode && !formData.code) {
      handleChange('code', generateCode(name));
    }
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Vendor name is required');
      return;
    }
    if (!formData.code) {
      toast.error('Vendor code is required');
      return;
    }

    setSaving(true);
    try {
      const url = isEditMode 
        ? `${API_URL}/api/catalog/vendor-sources/${vendorId}`
        : `${API_URL}/api/catalog/vendor-sources`;
      
      const res = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await res.json();
      
      if (res.ok) {
        toast.success(isEditMode ? 'Vendor updated' : 'Vendor created');
        onSave?.(data.vendor_source || data);
        onOpenChange(false);
      } else {
        toast.error(data.detail || 'Failed to save vendor');
      }
    } catch (error) {
      toast.error('Error saving vendor');
      console.error(error);
    }
    setSaving(false);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="vendor-form-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Truck className="w-5 h-5" />
            {isEditMode ? 'Edit Vendor' : 'Add New Vendor'}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Vendor Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g., Crown Awards"
                  data-testid="vendor-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="code">Code *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
                  placeholder="e.g., CRW"
                  maxLength={4}
                  className="font-mono uppercase"
                  data-testid="vendor-code-input"
                />
                <p className="text-xs text-gray-500 mt-1">3-4 letter code for SKUs</p>
              </div>

              <div>
                <Label htmlFor="vendor_type">Type</Label>
                <Select 
                  value={formData.vendor_type} 
                  onValueChange={(v) => handleChange('vendor_type', v)}
                >
                  <SelectTrigger data-testid="vendor-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VENDOR_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Website & Markup */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={formData.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                  placeholder="https://www.vendor.com"
                  data-testid="vendor-website-input"
                />
              </div>
              
              {formData.vendor_type !== 'white_label' && (
                <div>
                  <Label htmlFor="markup">Default Markup %</Label>
                  <Input
                    id="markup"
                    type="number"
                    min="0"
                    value={formData.default_markup_percent}
                    onChange={(e) => handleChange('default_markup_percent', parseInt(e.target.value) || 0)}
                    data-testid="vendor-markup-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Applied to vendor costs</p>
                </div>
              )}
            </div>

            {/* Contact Info */}
            <div className="border-t pt-4">
              <Label className="text-sm font-medium mb-3 block">Contact Information</Label>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="contact_name" className="text-xs">Name</Label>
                  <Input
                    id="contact_name"
                    value={formData.contact.name}
                    onChange={(e) => handleContactChange('name', e.target.value)}
                    placeholder="John Doe"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_email" className="text-xs">Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={formData.contact.email}
                    onChange={(e) => handleContactChange('email', e.target.value)}
                    placeholder="john@vendor.com"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_phone" className="text-xs">Phone</Label>
                  <Input
                    id="contact_phone"
                    value={formData.contact.phone}
                    onChange={(e) => handleContactChange('phone', e.target.value)}
                    placeholder="555-123-4567"
                    className="text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Any additional notes about this vendor..."
                rows={2}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-vendor-btn">
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" /> {isEditMode ? 'Update' : 'Create'} Vendor</>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VendorSourceFormModal;
